/**
 * init — scaffold a new agent-contracts-runtime project
 */

import { existsSync } from "node:fs";
import { mkdir, writeFile, readFile, appendFile } from "node:fs/promises";
import { join, relative } from "node:path";
export interface InitOptions {
  adapter: string;
  outputDir: string;
  force: boolean;
}

export interface InitResult {
  files_created: string[];
}

interface FileEntry {
  relativePath: string;
  content: string;
}

function buildConfigYaml(_adapter: string): string {
  return `plugins:
  - ./agent/src/plugins/example-plugin.ts
recovery:
  max_follow_ups: 2
  max_retries: 0
# model_mapping:
#   standard:
#     adapter: claude
#     model: claude-opus-4-6
model_mapping: {}
`;
}

const EXAMPLE_PLUGIN_TS = `\
import type { AgentPlugin } from "agent-contracts-runtime";

/**
 * Example plugin — extend agent-contracts-runtime with custom hooks.
 *
 * Register lifecycle hooks for tasks and workflows:
 *   beforeTask / afterTask — pre/post-process task execution
 *   contextEnhancer        — augment task context before prompt generation
 *   promptEnhancer         — post-process generated prompts
 *   customGuardrails       — add project-specific guardrail checks
 *   beforeWorkflow / afterWorkflow — workflow lifecycle hooks
 *
 * See https://github.com/foo-log-inc/agent-contracts-runtime#plugins
 */
export const examplePlugin: AgentPlugin = {
  id: "example-plugin",

  // Uncomment hooks as needed:

  // async beforeTask(taskId, context) {
  //   console.log(\`[example-plugin] beforeTask: \${taskId}\`);
  //   return context;
  // },

  // async afterTask(taskId, outcome) {
  //   console.log(\`[example-plugin] afterTask: \${taskId} — \${outcome.status}\`);
  //   return outcome;
  // },

  // promptEnhancer(taskId, prompt, _context) {
  //   return prompt + "\\n\\n<!-- enhanced by example-plugin -->";
  // },
};

export default examplePlugin;
`;

const CUSTOM_GUARDRAILS_TS = `\
import type { AgentPlugin, GuardrailResult } from "agent-contracts-runtime";

/**
 * Custom guardrails — project-specific safety checks.
 *
 * Implement evaluateCommand, evaluateFilePath, or evaluateFileContent
 * to enforce project rules beyond the built-in guardrails.
 *
 * See https://github.com/foo-log-inc/agent-contracts-runtime#guardrails
 */
export const customGuardrailsPlugin: AgentPlugin = {
  id: "custom-guardrails",

  customGuardrails: {
    // evaluateCommand(command: string): GuardrailResult[] {
    //   const results: GuardrailResult[] = [];
    //   // Example: block dangerous patterns
    //   if (command.includes("DROP TABLE")) {
    //     results.push({
    //       matched: true,
    //       guardrail_id: "no-drop-table",
    //       severity: "critical",
    //       action: "block",
    //       message: "DROP TABLE is forbidden",
    //     });
    //   }
    //   return results;
    // },
  },
};

export default customGuardrailsPlugin;
`;

const GITIGNORE_ENTRIES = [
  "",
  "# agent-contracts-runtime generated files",
  "agent/generated/",
];

function buildScaffoldFiles(adapter: string): FileEntry[] {
  return [
    {
      relativePath: "agents.conf.yaml",
      content: buildConfigYaml(adapter),
    },
    {
      relativePath: "agent/src/plugins/example-plugin.ts",
      content: EXAMPLE_PLUGIN_TS,
    },
    {
      relativePath: "agent/src/guardrails/custom-guardrails.ts",
      content: CUSTOM_GUARDRAILS_TS,
    },
  ];
}

export async function runInit(options: InitOptions): Promise<InitResult> {
  const { adapter, outputDir, force } = options;
  const files_created: string[] = [];

  const entries = buildScaffoldFiles(adapter);

  for (const entry of entries) {
    const absPath = join(outputDir, entry.relativePath);
    if (!force && existsSync(absPath)) {
      throw new Error(
        `File already exists: ${entry.relativePath}. Use --force to overwrite.`,
      );
    }
  }

  for (const entry of entries) {
    const absPath = join(outputDir, entry.relativePath);
    const dir = join(absPath, "..");
    await mkdir(dir, { recursive: true });
    await writeFile(absPath, entry.content, "utf8");
    files_created.push(entry.relativePath);
  }

  const gitignorePath = join(outputDir, ".gitignore");
  if (existsSync(gitignorePath)) {
    const existing = await readFile(gitignorePath, "utf8");
    if (!existing.includes("agent/generated/")) {
      await appendFile(gitignorePath, GITIGNORE_ENTRIES.join("\n") + "\n", "utf8");
      files_created.push(".gitignore");
    }
  } else {
    await writeFile(gitignorePath, GITIGNORE_ENTRIES.join("\n").trimStart() + "\n", "utf8");
    files_created.push(".gitignore");
  }

  return { files_created };
}
